import { useEffect, useState } from "react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselThumbnails,
  cn,
  type CarouselApi,
} from "@racoon/ui";
import { IcNotUploaded } from "@racoon/icons";
import { PhotoSlider } from "./photoSlider";

export const PhotoCarousel = ({
  title,
  photos,
  className,
  onPhotoClick,
}: {
  title?: string;
  photos: string[];
  className?: string;
  onPhotoClick?: (index: number) => void;
}) => {
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [isSliderOpen, setIsSliderOpen] = useState(false);
  const [sliderStartIndex, setSliderStartIndex] = useState(0);

  useEffect(() => {
    if (!carouselApi) return;
    setCurrent(carouselApi.selectedScrollSnap());
    const onSelect = () => setCurrent(carouselApi.selectedScrollSnap());
    carouselApi.on("select", onSelect);
    carouselApi.on("reInit", onSelect);
    return () => {
      carouselApi.off("select", onSelect);
      carouselApi.off("reInit", onSelect);
    };
  }, [carouselApi]);

  // No photos in this group: keep the slot, so the carousels of a job stay
  // aligned instead of collapsing whenever one group is missing.
  if (photos.length === 0) {
    return (
      <div className={cn("space-y-2 min-w-0", className)}>
        {title && <p className="text-sm font-medium text-text-secondary">{title}</p>}
        <div className="w-full aspect-[4/3] rounded-lg bg-surface-tertiary grid place-items-center">
          <IcNotUploaded className="text-brand-red" viewBox="0 0 24 24" width={32} height={32} />
        </div>
      </div>
    );
  }

  const openSliderAt = (index: number) => {
    if (onPhotoClick) {
      onPhotoClick(index);
      return;
    }
    setSliderStartIndex(index);
    setIsSliderOpen(true);
  };

  return (
    <div className={cn("space-y-2 min-w-0", className)}>
      {title && <p className="text-sm font-medium text-text-secondary">{title}</p>}
      <Carousel setApi={setCarouselApi} opts={{ align: "start" }} className="w-full">
        <CarouselContent>
          {photos.map((url, index) => (
            <CarouselItem key={`${url}-${index}`}>
              <img
                src={url}
                alt={title ? `${title} ${index + 1}` : `${index + 1}`}
                className="w-full aspect-[4/3] rounded-lg object-cover bg-slate-100 cursor-pointer"
                loading="lazy"
                onClick={() => openSliderAt(index)}
              />
            </CarouselItem>
          ))}
        </CarouselContent>
        {photos.length > 1 && (
          <CarouselThumbnails
            mainApi={carouselApi}
            className="w-full absolute bottom-2 left-0 flex gap-2 justify-center"
          >
            {photos.map((url, index) => (
              <div
                key={`thumb-${url}-${index}`}
                className={`bg-white relative size-2 rounded-full overflow-hidden transition-all ${
                  index === current ? "!bg-brand-blue shadow-lg" : ""
                }`}
              ></div>
            ))}
          </CarouselThumbnails>
        )}
      </Carousel>

      {!onPhotoClick && (
        <PhotoSlider
          title={title}
          open={isSliderOpen}
          onOpenChange={setIsSliderOpen}
          photos={photos}
          initialSlide={sliderStartIndex}
        />
      )}
    </div>
  );
};
